import axios from "axios";
import { ArrowLeft, BarChart3, Coins } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CarregandoBloco } from "@/components/carregando";
import { TIPOS_CLIENTE, nomesTipos, tipoDaUrl } from "@/features/tipos-cliente";
import { CashbackConfigModal } from "@/pages/Promocoes";

import { ROTA_FIDELIZE } from "./rotas";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const MODULOS = { Cardapio: "Cardápio", Delivery: "Delivery", Mesas: "Mesas", Balcao: "Balcão", Totem: "Totem", MesaQR: "Mesa QR", Comandas: "Comandas", ChatBot: "WhatsApp" };

function Linha({ rotulo, valor }) {
  return (
    <div className="flex justify-between gap-3 py-2 border-b last:border-0 text-sm">
      <span className="text-muted-foreground">{rotulo}</span>
      <span className="font-semibold text-right">{valor}</span>
    </div>
  );
}

// Fidelize seu cliente → Cashback. A configuração continua no mesmo modal
// (uma config só), agora com página própria em vez de aba.
export default function CashbackPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [aberto, setAberto] = useState(false);
  const [config, setConfig] = useState({ ativo: false, percentual: 0, modulos: ["Cardapio"] });
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  // Carregamento com cards só na primeira abertura (antes aparecia "Desligado" até a config chegar).
  const [primeiraCarga, setPrimeiraCarga] = useState(true);

  useEffect(() => {
    // Com o modal aberto, quem busca e edita a config é ele (buscar aqui também
    // apagava o tipo que veio marcado da tela de Clientes)
    if (aberto) return;
    axios.get("/api/cashback/config", auth())
      .then((r) => r.data && setConfig((c) => ({ ...c, ...r.data, ...(c.preSegmento ? { segmentos: [c.preSegmento] } : {}) })))
      .catch(() => {})
      .finally(() => setPrimeiraCarga(false));
  }, [aberto]); // recarrega ao fechar o modal — quem salva é ele

  // Vindo do botão de ofertas da tela de Clientes (?para=recorrente): abre já com o tipo marcado
  useEffect(() => {
    const para = tipoDaUrl(params);
    if (!para) return;
    setConfig((c) => ({ ...c, preSegmento: para }));
    setAberto(true);
    setParams((p) => { const n = new URLSearchParams(p); n.delete("para"); return n; }, { replace: true });
  }, [params, setParams]);
  // Ao FECHAR o modal o tipo pré-marcado sai (rodar já na abertura apagava a marcação)
  const estavaAberto = useRef(false);
  useEffect(() => {
    if (estavaAberto.current && !aberto) setConfig((c) => (c.preSegmento ? { ...c, preSegmento: null } : c));
    estavaAberto.current = aberto;
  }, [aberto]);

  const ativo = !!config.ativo;
  const porTipo = Object.entries(config.por_segmento || {}).filter(([k]) => TIPOS_CLIENTE[k]);
  const modulos = (config.modulos || []).map((m) => MODULOS[m] || m);

  return (
    <div className="p-4 sm:p-6" data-e2e="fidelize-cashback">
      <div className="max-w-3xl mx-auto space-y-5">
        <div>
          <button type="button" onClick={() => navigate(ROTA_FIDELIZE)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3">
            <ArrowLeft className="w-4 h-4" /> Fidelize seu cliente
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
              <Coins className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold leading-tight">Cashback</h1>
              <p className="text-sm text-muted-foreground">O cliente ganha uma parte de volta em crédito e volta pra gastar na sua loja.</p>
            </div>
          </div>
        </div>

        {primeiraCarga ? <CarregandoBloco altura="h-[96px]" /> : (
        <section className={`rounded-2xl border-2 p-5 ${ativo ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-card"}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Situação</p>
              <p className="text-2xl font-bold mt-0.5">
                {ativo ? <>Ligado · <span className="text-emerald-600 dark:text-emerald-400">{Number(config.percentual || 0)}% de volta</span></> : "Desligado"}
              </p>
            </div>
            <button type="button" onClick={() => setAberto(true)} data-e2e="configurar-cashback"
              className="h-11 px-5 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90">
              {ativo ? "Editar cashback" : "Ligar cashback"}
            </button>
          </div>
          {ativo && (
            <div className="mt-4">
              <Linha rotulo="Volta pro cliente" valor={`${Number(config.percentual || 0)}% de cada pedido`} />
              <Linha rotulo="Vale em" valor={modulos.length ? modulos.join(", ") : "Nenhum módulo"} />
              <Linha rotulo="Quem ganha" valor={nomesTipos(config.segmentos)} />
              {porTipo.length > 0 && (
                <Linha rotulo="Percentual por tipo" valor={porTipo.map(([k, v]) => `${TIPOS_CLIENTE[k].nome}: ${String(v).replace(".", ",")}%`).join(" · ")} />
              )}
            </div>
          )}
        </section>
        )}

        <section className="rounded-xl border bg-card p-5">
          <h2 className="font-semibold">Como funciona</h2>
          <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
            <li>• O cliente cadastrado recebe a porcentagem em crédito quando o pedido é concluído.</li>
            <li>• Ele vê o saldo no cardápio e escolhe usar no próximo pedido.</li>
            <li>• Cupom e cashback não se somam no mesmo pedido, e itens em promoção não geram crédito.</li>
            <li>• Dá para escolher quem ganha (os tipos da tela de Clientes) e um percentual diferente para cada tipo. O tipo é conferido quando o pedido é concluído.</li>
          </ul>
          <button type="button" onClick={() => navigate("/admin/impulsione/relatorio-ofertas/cashback")}
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
            <BarChart3 className="w-4 h-4" /> Ver o relatório de cashback
          </button>
        </section>
      </div>

      <CashbackConfigModal open={aberto} onOpenChange={setAberto} config={config} setConfig={setConfig}
        loading={carregando} setLoading={setCarregando} saving={salvando} setSaving={setSalvando} />
    </div>
  );
}
